/**
 * Browse View
 * Grid-based profile browsing
 */

import { render, showToast, refreshLikesCount, updateBrowseCount } from '../main';
import { TOAST_FAILED_LOAD_MORE, TOAST_ADD_LOCATION_FOR_DISTANCE } from '../utils/app-strings';
import { searchApi, BrowseProfile, SearchParams } from '../api';
import { navigate } from '../router';
import { loadingSpinner, emptyState, DISTANCE_SLIDER } from '../utils';
import { renderBrowseCard, setupBrowseCardClickHandlers } from '../components/browse-card';
import { store } from '../state';
import { renderSortMenu, getSortLabel as getSortLabelFromRows, setupSortMenuHandlers } from '../components/sort-menu';
import { applyPreferenceFilters, UserPrefs } from '../utils/search-params';

// Sort types
type SortKey = 'activity-desc' | 'activity-asc' | 'distance-asc' | 'distance-desc'
             | 'name-asc' | 'name-desc' | 'age-asc' | 'age-desc'
             | 'answered-desc' | 'answered-asc';

type LikesSortKey = 'liked_at-desc' | 'liked_at-asc' | 'distance-asc' | 'distance-desc'
                  | 'name-asc' | 'name-desc' | 'age-asc' | 'age-desc'
                  | 'answered-desc' | 'answered-asc';

// State
let profiles: BrowseProfile[] = [];
let isLoading = false;
let hasMore = true;
const DEFAULT_BROWSE_SORT: SortKey = 'activity-desc';
const DEFAULT_LIKES_SORT: LikesSortKey = 'liked_at-desc';
let currentSortKey: SortKey = DEFAULT_BROWSE_SORT;
let likesSortKey: LikesSortKey = DEFAULT_LIKES_SORT;
const PAGE_SIZE = 100;

// Grid view columns (persisted in localStorage)
const VIEW_OPTIONS = [1, 3, 5] as const;
let currentViewCols: number = parseInt(localStorage.getItem('browseViewCols') || '3', 10);

// Current mode: 'browse' or 'likes'
let currentMode: 'browse' | 'likes' = 'browse';

export function getBrowseMode(): 'browse' | 'likes' { return currentMode; }

// User preferences for search filters (loaded once per session)
let userPrefs: UserPrefs | null = null;

const SHARED_SORT_TAIL: { key: string; icon: string; label: string }[][] = [
  [
    { key: 'distance-asc', icon: 'location-arrow', label: 'Nearest' },
    { key: 'distance-desc', icon: 'map-marker-alt', label: 'Furthest' },
  ],
  [
    { key: 'name-asc', icon: 'sort-alpha-down', label: 'Name A-Z' },
    { key: 'name-desc', icon: 'sort-alpha-up', label: 'Name Z-A' },
  ],
  [
    { key: 'age-asc', icon: 'user', label: 'Youngest' },
    { key: 'age-desc', icon: 'user-clock', label: 'Age Oldest' },
  ],
  [
    { key: 'answered-desc', icon: 'check-circle', label: 'Most Answered' },
    { key: 'answered-asc', icon: 'circle', label: 'Least Answered' },
  ],
];

const SORT_ROWS = [
  [
    { key: 'activity-desc', icon: 'clock', label: 'Recent' },
    { key: 'activity-asc', icon: 'history', label: 'Oldest' },
  ],
  ...SHARED_SORT_TAIL,
];

const LIKES_SORT_ROWS = [
  [
    { key: 'liked_at-desc', icon: 'clock', label: 'Recent' },
    { key: 'liked_at-asc', icon: 'history', label: 'Oldest' },
  ],
  ...SHARED_SORT_TAIL,
];

function getSortLabel(key: string, isLikesMode = false): string {
  return getSortLabelFromRows(isLikesMode ? LIKES_SORT_ROWS : SORT_ROWS, key);
}

// Track current filter count
let filterCount = 0;

/** Count active filters (non-default values) */
function countActiveFilters(discovery: Record<string, any> | undefined): number {
  if (!discovery) return 0;

  let count = 0;

  // Boolean toggles
  if (discovery.two_way_match === true) count++;
  if (discovery.inclusive_of_trans_nb === true) count++;
  if (discovery.open_to_enm_poly === true) count++;

  // Distance (only count if not unlimited)
  if (discovery.distance_max_km && discovery.distance_max_km < DISTANCE_SLIDER.UNLIMITED) count++;

  // Height range
  if (discovery.height_min_cm || discovery.height_max_cm) count++;

  // Recently active
  if (discovery.recently_active_hours) count++;

  // Single-value filters
  if (discovery.has_kids) count++;
  if (discovery.preferred_structure) count++;

  // Array filters (count if any selected)
  const arrayFields = [
    'interested_in', 'relationship_intents', 'wants_more_kids',
    'smoking', 'drinking', 'cannabis', 'drugs',
    'open_to_long_distance', 'open_to_relocation',
    'education_levels', 'employment_statuses', 'languages',
    'pets_ok_with', 'religions', 'ethnicities', 'political_views',
    'income_ranges', 'body_types', 'sexual_orientations',
    'sexual_roles', 'sexual_dynamics',
  ];

  for (const field of arrayFields) {
    if (Array.isArray(discovery[field]) && discovery[field].length > 0) count++;
  }

  // Activity filters
  if (discovery.activity_liked_me === true && currentMode !== 'likes') count++;
  if (discovery.activity_i_liked === true) count++;
  if (discovery.activity_i_disliked === true) count++;

  // Travel location override
  if (discovery.travel_city || discovery.travel_lat != null) count++;

  return count;
}

/** Check if user has a location (profile or travel override) */
function hasLocation(): boolean {
  if (!userPrefs) return false;
  const filters = userPrefs.filters;
  if (filters?.travel_lat != null && filters?.travel_lng != null) return true;
  return userPrefs.latitude != null && userPrefs.longitude != null;
}

/** Load user preferences from store and update filter count */
function loadUserPrefs(): void {
  const state = store.getState();
  const profile = state.user?.profile;
  const prefs = state.user?.preferences;
  const filters = prefs?.filters_json || {};

  userPrefs = {
    age_min: prefs?.age_min,
    age_max: prefs?.age_max,
    distance_max_km: prefs?.distance_max_km,
    latitude: profile?.latitude,
    longitude: profile?.longitude,
    gender_id: profile?.gender_id,
    birth_date: profile?.birth_date,
    height_cm: profile?.height_cm,
    filters,
  };

  // Auto-switch to distance sort when travel location is active
  if (filters?.travel_lat != null && filters?.travel_lng != null) {
    currentSortKey = 'distance-asc';
    const sortLabel = document.getElementById('sort-label');
    if (sortLabel) sortLabel.textContent = getSortLabel(currentSortKey);
  }

  filterCount = countActiveFilters(filters);
  updateFilterLabel();
}

/** Update the filter count label in the UI */
function updateFilterLabel(): void {
  const label = document.getElementById('filters-label');
  if (label) label.textContent = `(${filterCount})`;
}

/** Render the shared browse header (Filter/View/Sort pills) */
function renderBrowseHeader(sortLabel: string): string {
  return `
    <div class="browse-header">
      <button class="browse-pill" id="filters-btn">
        <span class="browse-pill-main">Filter <i class="fas fa-sliders-h"></i></span>
        <span class="browse-pill-label" id="filters-label">(${filterCount})</span>
      </button>
      <button class="browse-pill" id="view-btn">
        <span class="browse-pill-main">View <i class="fas fa-th"></i></span>
        <span class="browse-pill-label" id="view-label">(${currentViewCols})</span>
      </button>
      <button class="browse-pill" id="sort-btn">
        <span class="browse-pill-main">Sort <i class="fas fa-sort"></i></span>
        <span class="browse-pill-label" id="sort-label">${sortLabel}</span>
      </button>
    </div>
  `;
}

/** Render the view menu dropdown HTML */
function renderViewMenu(): string {
  const labels: Record<number, string> = { 1: '1 column', 3: '3 columns', 5: '5 columns' };
  return `
    <div class="seg-rows" id="view-menu" style="display: none;">
      <div class="seg-row">
        ${VIEW_OPTIONS.map(cols => `
          <button class="seg-btn ${cols === currentViewCols ? 'active' : ''}" data-cols="${cols}">
            ${labels[cols] || cols}
          </button>
        `).join('')}
      </div>
    </div>
  `;
}

/** Apply the selected grid column count */
function applyGridView(cols: number): void {
  const grid = document.getElementById('browse-grid');
  if (!grid) return;
  grid.style.gridTemplateColumns = `repeat(${cols}, 1fr)`;
  grid.classList.toggle('browse-grid-compact', cols >= 5);
  localStorage.setItem('browseViewCols', String(cols));
  currentViewCols = cols;

  // Update pill label
  const viewLabel = document.getElementById('view-label');
  if (viewLabel) viewLabel.textContent = `(${cols})`;
}

/** Reset browse sort to default (activity-desc / "Recent") */
export function resetBrowseSort(): void {
  currentSortKey = DEFAULT_BROWSE_SORT;
}

export async function renderBrowse(): Promise<void> {
  currentMode = 'browse';

  // Check for saved scroll position from browse context (returning from profile view)
  const context = store.getBrowseContext();
  const savedScrollPosition = context?.returnTo === '/browse' ? context.scrollPosition : undefined;

  // Reset state on initial render
  profiles = [];
  hasMore = true;
  isLoading = false;

  render(`
    <div class="view-frame browse-wrapper">
      ${renderBrowseHeader(getSortLabel(currentSortKey))}
      ${renderViewMenu()}
      ${renderSortMenu(SORT_ROWS, currentSortKey)}
      <div class="view-scroll browse-grid" id="browse-grid">
        ${loadingSpinner()}
      </div>
    </div>
  `);

  setupEventHandlers();
  loadUserPrefs(); // Load user prefs for filter count and search params
  await loadProfiles();

  // Restore scroll position if returning from profile view
  if (savedScrollPosition !== undefined) {
    requestAnimationFrame(() => {
      const grid = document.querySelector('.browse-grid');
      if (grid) {
        grid.scrollTop = savedScrollPosition;
      }
    });
    // Clear the browse context since we've restored
    store.setBrowseContext(null);
  }
}

/** Render Likes mode - people who liked me, no interaction from me */
export async function renderLikes(): Promise<void> {
  currentMode = 'likes';

  // Check for saved scroll position from browse context (returning from profile view)
  const context = store.getBrowseContext();
  const savedScrollPosition = context?.returnTo === '/likes' ? context.scrollPosition : undefined;

  // Reset state on initial render
  profiles = [];
  hasMore = true;
  isLoading = false;

  render(`
    <div class="view-frame browse-wrapper">
      ${renderBrowseHeader(getSortLabel(likesSortKey, true))}
      ${renderViewMenu()}
      ${renderSortMenu(LIKES_SORT_ROWS, likesSortKey)}
      <div class="view-scroll browse-grid" id="browse-grid">
        ${loadingSpinner()}
      </div>
    </div>
  `);

  setupEventHandlers();
  loadUserPrefs(); // Load user prefs for filter count and search params
  await loadProfiles();

  // Restore scroll position if returning from profile view
  if (savedScrollPosition !== undefined) {
    requestAnimationFrame(() => {
      const grid = document.querySelector('.browse-grid');
      if (grid) {
        grid.scrollTop = savedScrollPosition;
      }
    });
    // Clear the browse context since we've restored
    store.setBrowseContext(null);
  }
}

async function loadProfiles(append = false): Promise<void> {
  if (isLoading || (!append && profiles.length > 0 && !hasMore)) return;

  isLoading = true;
  const grid = document.getElementById('browse-grid')!;

  if (!append) {
    grid.innerHTML = loadingSpinner();
  } else {
    // Add loading indicator at the bottom
    const loadingEl = document.createElement('div');
    loadingEl.id = 'load-more-spinner';
    loadingEl.className = 'loading';
    loadingEl.innerHTML = '<div class="spinner"></div>';
    grid.appendChild(loadingEl);
  }

  try {
    // Use the appropriate sort key based on mode
    const sortKey = currentMode === 'likes' ? likesSortKey : currentSortKey;
    const [sort, direction] = sortKey.split('-') as [SearchParams['sort'], SearchParams['direction']];

    // Build search params based on mode
    const searchParams: SearchParams = {
      limit: PAGE_SIZE,
      offset: append ? profiles.length : 0,
      sort,
      direction,
    };

    // Apply user's preference filters (shared by Browse and Likes modes)
    if (userPrefs) {
      applyPreferenceFilters(searchParams, userPrefs);
    }

    if (currentMode === 'likes') {
      // Likes mode: also require people who liked me with no interaction from me
      searchParams.liked_me = true;
      searchParams.no_my_interaction = true;
    }

    const response = await searchApi.browse(searchParams);

    const newProfiles = response.data.profiles;
    hasMore = response.data.hasMore;

    // Update browse badge with accurate total on initial load (not append, not likes mode)
    if (!append && currentMode === 'browse') {
      updateBrowseCount(response.data.total);
    }

    if (append) {
      profiles = [...profiles, ...newProfiles];
      // Remove loading indicator
      document.getElementById('load-more-spinner')?.remove();
      // Append new cards
      newProfiles.forEach(profile => {
        const cardHtml = renderBrowseCard(profile);
        const temp = document.createElement('div');
        temp.innerHTML = cardHtml;
        const card = temp.firstElementChild as HTMLElement;
        grid.appendChild(card);
      });
    } else {
      profiles = newProfiles;
      renderGrid();
    }

    setupBrowseCardClickHandlers(navigate, profiles, currentMode === 'likes' ? '/likes' : '/browse');

    // Add load more button if there are more results
    if (hasMore) {
      addLoadMoreButton();
    }
  } catch (err: any) {
    console.error('Failed to load profiles:', err);
    if (!append) {
      grid.innerHTML = emptyState({
        icon: 'exclamation-circle',
        title: 'Something went wrong',
        message: 'Unable to load profiles',
        buttonText: 'Retry',
        buttonId: 'retry-btn',
      });
      document.getElementById('retry-btn')?.addEventListener('click', () => loadProfiles());
    } else {
      document.getElementById('load-more-spinner')?.remove();
      showToast(TOAST_FAILED_LOAD_MORE, 'error');
    }
  } finally {
    isLoading = false;
  }
}

function renderGrid(): void {
  const grid = document.getElementById('browse-grid')!;
  if (profiles.length === 0) {
    if (currentMode === 'likes') {
      // Likes mode empty state
      grid.innerHTML = emptyState({
        icon: 'inbox',
        title: 'No interest yet',
        message: 'When someone is interested in you, they\'ll appear here',
      });
    } else {
      // Browse mode empty state
      grid.innerHTML = emptyState({
        icon: 'users',
        title: 'No profiles found',
        message: 'Try adjusting your filters',
        buttonText: 'Adjust Filters',
        buttonId: 'adjust-filters-btn',
      });
      document.getElementById('adjust-filters-btn')?.addEventListener('click', () => {
        navigate('/filters');
      });
    }
    return;
  }

  grid.innerHTML = profiles.map(profile => renderBrowseCard(profile)).join('');

  // Apply persisted grid view
  applyGridView(currentViewCols);

  setupBrowseCardClickHandlers(navigate, profiles, currentMode === 'likes' ? '/likes' : '/browse');

  if (hasMore) {
    addLoadMoreButton();
  }
}

let loadMoreObserver: IntersectionObserver | null = null;

function addLoadMoreButton(): void {
  const grid = document.getElementById('browse-grid')!;

  // Remove existing sentinel if present
  document.getElementById('load-more-sentinel')?.remove();

  const sentinel = document.createElement('div');
  sentinel.id = 'load-more-sentinel';
  sentinel.className = 'browse-load-more-sentinel';
  grid.appendChild(sentinel);

  // Clean up previous observer
  loadMoreObserver?.disconnect();

  const scrollRoot = grid.closest('.view-scroll') as HTMLElement | null;

  loadMoreObserver = new IntersectionObserver(
    (entries) => {
      if (entries[0].isIntersecting && !isLoading && hasMore) {
        loadProfiles(true);
      }
    },
    { root: scrollRoot, rootMargin: '200px' },
  );
  loadMoreObserver.observe(sentinel);
}

function setupEventHandlers(): void {
  // Sort menu (close view menu when sort opens)
  setupSortMenuHandlers(
    () => currentMode === 'likes' ? likesSortKey : currentSortKey,
    async (newKey) => {
      // Block distance sorts when user has no location
      if ((newKey === 'distance-asc' || newKey === 'distance-desc') && !hasLocation()) {
        showToast(TOAST_ADD_LOCATION_FOR_DISTANCE, 'error', 4000);
        // Revert sort label back to current
        const sortLabel = document.getElementById('sort-label');
        const activeKey = currentMode === 'likes' ? likesSortKey : currentSortKey;
        if (sortLabel) sortLabel.textContent = getSortLabel(activeKey, currentMode === 'likes');
        // Revert active button state
        document.querySelectorAll('#sort-menu .seg-btn').forEach(opt => {
          opt.classList.toggle('active', (opt as HTMLElement).dataset.sort === activeKey);
        });
        return;
      }

      if (currentMode === 'likes') {
        likesSortKey = newKey as LikesSortKey;
      } else {
        currentSortKey = newKey as SortKey;
      }
      profiles = [];
      hasMore = true;
      await loadProfiles();
      const grid = document.getElementById('browse-grid');
      if (grid) grid.scrollTop = 0;
    },
    ['view-menu'],
  );

  // View menu toggle (close sort menu when view opens)
  document.getElementById('view-btn')?.addEventListener('click', () => {
    const menu = document.getElementById('view-menu')!;
    const sortMenu = document.getElementById('sort-menu');
    if (sortMenu) sortMenu.style.display = 'none';
    menu.style.display = menu.style.display === 'none' ? 'flex' : 'none';
  });

  // View option selection
  document.querySelectorAll('#view-menu .seg-btn').forEach(option => {
    option.addEventListener('click', () => {
      const cols = parseInt((option as HTMLElement).dataset.cols || '2', 10);
      if (cols === currentViewCols) {
        document.getElementById('view-menu')!.style.display = 'none';
        return;
      }

      // Update active state
      document.querySelectorAll('#view-menu .seg-btn').forEach(opt => opt.classList.remove('active'));
      option.classList.add('active');

      // Hide menu
      document.getElementById('view-menu')!.style.display = 'none';

      applyGridView(cols);
    });
  });

  // Close view menu on outside click
  document.addEventListener('click', (e) => {
    const viewBtn = document.getElementById('view-btn');
    const viewMenu = document.getElementById('view-menu');
    if (viewBtn && viewMenu && !viewBtn.contains(e.target as Node) && !viewMenu.contains(e.target as Node)) {
      viewMenu.style.display = 'none';
    }
  });

  // Filters button
  document.getElementById('filters-btn')?.addEventListener('click', () => {
    navigate('/filters');
  });
}
